/**
 * Citation entity model — ES module version of MyScripts/Entities/Citation.ts
 */

import type { CitationData, AttachmentData } from '@/services/api-types';

export type { CitationData };

// ── Sub-entity interfaces (not in api-types.ts) ─────────────────────

export interface OffenderSnapshotData {
  id: number;
  firstName: string;
  lastName: string;
  middleName: string;
  suffix: string;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  idType: number;
  idNumber: string;
  idState: string;
  employeeId: string;
  employer: string;
  gender: number;
  race: number;
  height: string;
  weight: string;
  hairColor: number;
  eyeColor: number;
}

export interface VehicleSnapshotData {
  plateNumber: string;
  plateState: string;
  plateStateValue: string;
  make: string;
  makeValue: string;
  model: string;
  modelValue: string;
  year: number;
  color: string;
  colorValue: string;
  vin: string;
}

export interface CitationListItemData {
  id: number;
  citationNumber: string | null;
  status: string;
  dateOfIssuance: string;
  offenderName: string;
  npsdPersonId: number;
  violationTypes: number[];
  violationTypeValues: string[];
  propertyLocation: string;
  offenseLocation: string;
  fineAmount: number | null;
  fineToBeDetermined: boolean;
  callCardId: number | null;
  eventId: string | null;
  issuingOfficerName: string;
  createdDate: string;
  modifiedDate: string;
  voidedBy: number | null;
  voidedByName: string | null;
}

export type TrespassStatus = 'police' | 'verbal' | 'none' | null;

export const CITATION_STATUS = {
  draft: 'draft',
  issued: 'issued',
  voided: 'voided',
} as const;

// ── Date field helpers ───────────────────────────────────────────────

export const CITATION_DATE_FIELDS = [
  'dateOfIssuance',
  'createdDate',
  'modifiedDate',
  'voidedDate',
  'offenseDateTime',
  'trespassDateTime',
  'courtDateTime',
] as const;

export function isCitationDateField(name: string): boolean {
  return (CITATION_DATE_FIELDS as readonly string[]).includes(name);
}

// ── Factory ──────────────────────────────────────────────────────────

export function createCitation(data?: Partial<CitationData>): CitationData {
  return {
    id: null,
    citationNumber: null,
    status: 'draft',
    voidedReason: null,
    voidedBy: null,
    voidedByName: null,
    voidedDate: null,
    createdBy: null,
    createdByName: null,
    createdDate: null,
    modifiedBy: null,
    modifiedByName: null,
    modifiedDate: null,
    dateOfIssuance: null,
    issuingOfficerId: null,
    issuingOfficerName: null,
    issuingOfficerBadgeNumber: null,
    issuingOfficerAgency: null,
    propertyLocation: null,
    npsdPersonId: null,
    offenderSnapshot: null,
    callCardId: null,
    violationTypes: [],
    otherViolation: null,
    offenseDateTime: null,
    offenseLocation: null,
    allegedSpeed: null,
    speedLimit: null,
    npsdVehicleId: null,
    vehicleSnapshot: null,
    fineAmount: null,
    fineToBeDetermined: false,
    probableCauseNarrative: null,
    bodyCamReference: null,
    cadEventNumber: null,
    attachmentList: [],
    trespassStatus: null,
    policeCaseNumber: null,
    trespassDateTime: null,
    signatureAttachmentId: null,
    signatureAttachmentActivationKey: null,
    signatureUrl: null,
    signatureRefused: false,
    notes: null,
    courtDateTime: null,
    ...data,
  };
}
